import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import startCase from 'lodash-es/startCase'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import colors from 'lib/colors';

const STATUS_STYLES: Record<
  string,
  {
    backgroundColor: string
    accentColor: string
    labelColor?: string
  }
> = {
  'new-lead': {
    backgroundColor: colors.lightOlivine,
    accentColor: colors.darkOlivine,
  },
  applied: {
    backgroundColor: colors.lightFrenchBlue,
    accentColor: colors.frenchBlue,
  },
  enrolled: {
    backgroundColor: colors.lightAmethyst,
    accentColor: colors.darkAmethyst,
  },
  started: {
    backgroundColor: colors.lightPink,
    accentColor: colors.darkPink,
  },
  filtered: {
    backgroundColor: colors.lightGoldenrod,
    accentColor: colors.darkGoldenrod,
  },
  returned: {
    backgroundColor: colors.lightApricot,
    accentColor: colors.darkApricot,
  },
  rejected: {
    backgroundColor: colors.lightCopperRed,
    accentColor: colors.darkCopperRed,
  },
  error: {
    backgroundColor: colors.lightCopperRed,
    accentColor: colors.darkCopperRed,
    labelColor: colors.darkCopperRed,
  },
}

const FALLBACK_STATUS_STYLE = {
  backgroundColor: colors.lightGray,
  accentColor: colors.slateGray,
}

export function getStatusStyle(status: string) {
  return STATUS_STYLES[status] ?? FALLBACK_STATUS_STYLE
}

type StatusFilterButtonProps = {
  status: string
  count: number
  showCount?: boolean
  selected?: boolean
  onClick?: () => void
}

export default function StatusFilterButton({
  status,
  count,
  showCount = true,
  selected = false,
  onClick,
}: StatusFilterButtonProps) {
  const statusStyle = getStatusStyle(status)
  const label = startCase(status)
  const labelColor = statusStyle.labelColor ?? 'inherit'

  return (
    <Chip
      clickable
      onClick={onClick}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, pr: !showCount ? 1 : 0 }}>
          {status === 'error' && (
            <ReportGmailerrorredIcon
              sx={{
                fontSize: 16,
                color: statusStyle.accentColor,
                position: 'relative',
                top: '-1px',
              }}
            />
          )}

          <Box
            component='span'
            sx={{
              color: labelColor,
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            {label}
          </Box>

          {showCount &&
            <Box
              component='span'
              sx={{
                backgroundColor: 'white',
                color: statusStyle.accentColor,
                borderRadius: '999px',
                minWidth: 28,
                height: 20,
                px: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {count.toLocaleString()}
            </Box>
          }
        </Box>
      }
      sx={(theme) => ({
        backgroundColor: statusStyle.backgroundColor,
        borderRadius: '999px',
        border: selected
          ? `2px solid ${statusStyle.accentColor}`
          : '2px solid transparent',
        height: 32,
        px: 0,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '& .MuiChip-label': { px: 1, py: 0 },
        '&:hover': {
          backgroundColor: statusStyle.backgroundColor,
          border: selected
            ? `2px solid ${statusStyle.accentColor}`
            : `2px solid ${statusStyle.accentColor}40`,
        },
        '& .MuiTouchRipple-root': {
          display: 'none',
        },
      })}
    />
  )
}
